import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { Helmet } from "react-helmet-async";
import ApiCall from "../../utils/apicall";
import { useSelector } from "react-redux";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  Container, Card, Stack, Typography, Button, TextField, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, InputAdornment, Box, Tooltip,
  Divider, Grid, MenuItem, Checkbox, FormControlLabel,
} from "@mui/material";
import Iconify from "../../components/iconify";
import { StatusPill, KpiCard } from "../../components/WeldAssure";
import { printWPS } from "../../utils/weldAssurePrint";
import { BrandedDialogHeader, SectionHeader, FIELD_SX } from "../../components/WeldAssure/BrandedForm";

import SmartTable from "../../components/SmartTable/SmartTable";
const EMPTY_FORM = {
  // header
  wpsNumber: "", revision: "", wpsDate: "", preparedBy: "", authorizedBy: "",
  authDate: "", prequalified: false, supportingPQRs: "", process: "", type: "",
  awsJointDetail: "", backGouge: "",
  // joint design
  jointType: "", jointBacking: "", backingMaterial: "", sides: "", rootOpening: "",
  rootFace: "", grooveAngle: "", radiusJU: "",
  // base metals
  baseMetalSpec: "", specFrom: "", gradeFrom: "", specTo: "", gradeTo: "",
  grooveThickness: "", filletSize: "", pipeDiameter: "",
  // filler
  awsSpec: "", awsClass: "", sfaSpec: "", altClass: "",
  // shielding
  flux: "", gas: "", gasComposition: "", flowRateCFH: "", gasCupSize: "", electrodeFlux: "",
  // preheat / interpass
  preheatMin: "", preheatUpTo34: "", preheatOver34: "", preheatOver112: "",
  preheatOver212: "", interpassMin: "", interpassMax: "",
  // position
  groovePos: "", filletPos: "", progression: "",
  // electrical
  transferMode: "", current: "", otherElec: "", tungstenSize: "", tungstenType: "",
  // technique
  stringerWeave: "", multiSinglePass: "", numElectrodes: "", ctwd: "", angle: "",
  peening: "", interpassClean: "",
  // pwht
  pwhtRequired: "", pwhtTemp: "", pwhtTime: "",
  // welding procedure (single representative pass)
  heatInput: "", procLayerPass: "", procProcess: "", procFillerClass: "", procDia: "",
  procCurType: "", procAmps: "", procVolts: "", procTravel: "",
  notes: "",
};

function buildDocumentData(form) {
  return {
    authDate: form.authDate, prequalified: form.prequalified,
    supportingPQRs: form.supportingPQRs, type: form.type,
    awsJointDetail: form.awsJointDetail, backGouge: form.backGouge,
    joint: {
      type: form.jointType, backing: form.jointBacking, backingMaterial: form.backingMaterial,
      sides: form.sides, rootOpening: form.rootOpening, rootFace: form.rootFace,
      grooveAngle: form.grooveAngle, radiusJU: form.radiusJU,
    },
    baseMetals: {
      specFrom: form.specFrom, gradeFrom: form.gradeFrom, specTo: form.specTo,
      gradeTo: form.gradeTo, grooveThickness: form.grooveThickness,
      filletSize: form.filletSize, pipeDiameter: form.pipeDiameter,
    },
    filler: { awsSpec: form.awsSpec, awsClass: form.awsClass, sfaSpec: form.sfaSpec, altClass: form.altClass },
    shielding: {
      flux: form.flux, gas: form.gas, composition: form.gasComposition,
      flowRateCFH: form.flowRateCFH, gasCupSize: form.gasCupSize, electrodeFlux: form.electrodeFlux,
    },
    preheat: {
      min: form.preheatMin, upTo34: form.preheatUpTo34, over34: form.preheatOver34,
      over112: form.preheatOver112, over212: form.preheatOver212,
      interpassMin: form.interpassMin, interpassMax: form.interpassMax,
    },
    position: { groove: form.groovePos, fillet: form.filletPos, progression: form.progression },
    electrical: {
      transferMode: form.transferMode, current: form.current, other: form.otherElec,
      tungstenSize: form.tungstenSize, tungstenType: form.tungstenType,
    },
    technique: {
      stringerWeave: form.stringerWeave, multiSinglePass: form.multiSinglePass,
      numElectrodes: form.numElectrodes, ctwd: form.ctwd, angle: form.angle,
      peening: form.peening, interpassClean: form.interpassClean,
    },
    pwht: { required: form.pwhtRequired, temp: form.pwhtTemp, time: form.pwhtTime },
    procedure: {
      heatInput: form.heatInput, layerPass: form.procLayerPass, process: form.procProcess,
      fillerClass: form.procFillerClass, dia: form.procDia, curType: form.procCurType,
      amps: form.procAmps, volts: form.procVolts, travel: form.procTravel,
    },
    // legacy keys (back-compat with older readers)
    fillerMetal: form.awsClass, shieldingGas: form.gas, notes: form.notes,
  };
}

function flattenDocumentData(doc) {
  const dd = doc.documentData || {};
  const j = dd.joint || {}, bm = dd.baseMetals || {}, fl = dd.filler || {};
  const sh = dd.shielding || {}, ph = dd.preheat || {}, pos = dd.position || {};
  const el = dd.electrical || {}, tc = dd.technique || {}, pw = dd.pwht || {}, pr = dd.procedure || {};
  const g = (x) => x || "";
  return {
    wpsNumber: g(doc.wpsNumber), revision: g(doc.revision), wpsDate: g(doc.wpsDate),
    preparedBy: g(doc.preparedBy), authorizedBy: g(doc.authorizedBy),
    authDate: g(dd.authDate), prequalified: dd.prequalified || false,
    supportingPQRs: g(dd.supportingPQRs), process: g(doc.process), type: g(dd.type),
    awsJointDetail: g(dd.awsJointDetail), backGouge: g(dd.backGouge),
    jointType: g(j.type), jointBacking: g(j.backing), backingMaterial: g(j.backingMaterial),
    sides: g(j.sides), rootOpening: g(j.rootOpening), rootFace: g(j.rootFace),
    grooveAngle: g(j.grooveAngle), radiusJU: g(j.radiusJU),
    baseMetalSpec: g(doc.baseMetalSpec), specFrom: g(bm.specFrom), gradeFrom: g(bm.gradeFrom),
    specTo: g(bm.specTo), gradeTo: g(bm.gradeTo), grooveThickness: g(bm.grooveThickness),
    filletSize: g(bm.filletSize), pipeDiameter: g(bm.pipeDiameter),
    awsSpec: g(fl.awsSpec), awsClass: g(fl.awsClass || dd.fillerMetal), sfaSpec: g(fl.sfaSpec), altClass: g(fl.altClass),
    flux: g(sh.flux), gas: g(sh.gas || dd.shieldingGas), gasComposition: g(sh.composition),
    flowRateCFH: g(sh.flowRateCFH), gasCupSize: g(sh.gasCupSize), electrodeFlux: g(sh.electrodeFlux),
    preheatMin: g(ph.min), preheatUpTo34: g(ph.upTo34), preheatOver34: g(ph.over34),
    preheatOver112: g(ph.over112), preheatOver212: g(ph.over212),
    interpassMin: g(ph.interpassMin), interpassMax: g(ph.interpassMax),
    groovePos: g(pos.groove), filletPos: g(pos.fillet), progression: g(pos.progression),
    transferMode: g(el.transferMode), current: g(el.current), otherElec: g(el.other),
    tungstenSize: g(el.tungstenSize), tungstenType: g(el.tungstenType),
    stringerWeave: g(tc.stringerWeave), multiSinglePass: g(tc.multiSinglePass),
    numElectrodes: g(tc.numElectrodes), ctwd: g(tc.ctwd), angle: g(tc.angle),
    peening: g(tc.peening), interpassClean: g(tc.interpassClean),
    pwhtRequired: g(pw.required), pwhtTemp: g(pw.temp), pwhtTime: g(pw.time),
    heatInput: g(pr.heatInput), procLayerPass: g(pr.layerPass), procProcess: g(pr.process),
    procFillerClass: g(pr.fillerClass), procDia: g(pr.dia), procCurType: g(pr.curType),
    procAmps: g(pr.amps), procVolts: g(pr.volts), procTravel: g(pr.travel),
    notes: g(dd.notes),
  };
}

const YN = ["", "Yes", "No"];
const SIDES = ["", "Single", "Double"];
const PROG = ["", "Up", "Down"];
const TYPES = ["", "Manual", "Semi-Auto", "Machine", "Automatic"];
const XFER = ["", "Short", "Globular", "Spray", "Pulsed"];
const CUR = ["", "AC", "DCEP", "DCEN", "Pulsed"];

export default function WPSDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // S-4 approval: Admin and Quality Manager only; the server enforces the same rule.
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveError, setApproveError] = useState("");
  const [approving, setApproving] = useState(false);
  const { loginUser } = useSelector((state) => state.userSlice);
  const userPermissions = loginUser?.decodedToken?.permissions ?? [];
  const canApprove = hasPermission(userPermissions, PERMISSIONS.APPROVE_WELD_PROCEDURE);

  const fetchDocs = async () => {
    try {
      setLoading(true);
      const res = await ApiCall.get("/wps", { params: { search } });
      setDocs(res.data?.data || res.data || []);
    } catch (err) { console.error("fetchWPS:", err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [search]);

  const handleOpen = async (doc = null) => {
    if (doc) {
      const res = await ApiCall.get(`/wps/${doc.id}`);
      const full = res.data?.data || res.data;
      setEditing(full);
      setFormData(flattenDocumentData(full));
    } else {
      setEditing(null);
      setFormData(EMPTY_FORM);
    }
    setOpenForm(true);
  };

  const handleClose = () => { setOpenForm(false); setEditing(null); setFormData(EMPTY_FORM); };
  const f = (field) => (e) => setFormData((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    if (!formData.wpsNumber.trim()) return;
    setSubmitLoading(true);
    try {
      const payload = {
        wpsNumber: formData.wpsNumber, revision: formData.revision,
        wpsDate: formData.wpsDate || null, preparedBy: formData.preparedBy,
        authorizedBy: formData.authorizedBy, process: formData.process,
        baseMetalSpec: formData.baseMetalSpec, documentData: buildDocumentData(formData),
      };
      if (editing) await ApiCall.put(`/wps/${editing.id}`, payload);
      else await ApiCall.post("/wps", payload);
      handleClose(); fetchDocs();
    } catch (err) { console.error("saveWPS:", err); } finally { setSubmitLoading(false); }
  };

  const handlePrint = async (d) => {
    try {
      const res = await ApiCall.get(`/wps/${d.id}`);
      printWPS(res.data?.data || res.data);
    } catch (err) { console.error("printWPS:", err); printWPS(d); }
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setApproving(true); setApproveError("");
    try { await ApiCall.post(`/wps/${approveTarget.id}/approve`, {}); setApproveTarget(null); fetchDocs(); }
    catch (err) { setApproveError(err?.response?.data?.message || "Approval failed. Try again."); }
    finally { setApproving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await ApiCall.delete(`/wps/${deleteTarget.id}`);
      setDeleteTarget(null); fetchDocs();
    } catch (err) { console.error("deleteWPS:", err); }
  };

  // compact field helpers for the dialog
  const Fld = ({ label, field, sm = 3, select, options = [], ...rest }) => (
    <Grid item xs={12} sm={sm}>
      <TextField
        label={label} value={formData[field] ?? ""} onChange={f(field)}
        fullWidth size="small" select={select || undefined} sx={FIELD_SX} {...rest}
      >
        {select && options.map((o) => <MenuItem key={o} value={o}>{o || "—"}</MenuItem>)}
      </TextField>
    </Grid>
  );
  const Sec = ({ children }) => (
    <Grid item xs={12}><SectionHeader>{children}</SectionHeader></Grid>
  );

  return (
    <>
      <Helmet><title>WPS Documents | WeldAssure</title></Helmet>
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">WPS Documents</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => handleOpen()}>
            New WPS
          </Button>
        </Stack>
        <PageGuide
          why="Keep approved welding procedures on file — so welders work to a qualified procedure, not from memory."
          who="Welding engineers and the QM maintaining procedures; welders follow them."
          what="Your Welding Procedure Specifications (WPS), each printable as a controlled document."
          how="Fill in the procedure, then Print PDF to produce the controlled WPS."
          when="Before welding to a procedure — and whenever a procedure is added or revised."
          standard="AWS D1.1 Clause 3 · ASME BPVC Section IX"
        />

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Active WPSs" value={docs.filter(d => !d.deletedAt && d.approvalStatus !== "superseded").length || docs.length} sub="ready to use" subKind="success" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Pending PQR" value={docs.filter(d => !d.supportingPQRs && !(d.documentData?.supportingPQRs)).length} sub="needs supporting PQR" subKind="warning" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Prequalified" value={docs.filter(d => d.documentData?.prequalified).length} sub="no PQR required" subKind="info" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="AWS Code" value="D1.1:2020" sub="all conformed" subKind="neutral" />
          </Grid>
        </Grid>

        <Card sx={{ borderColor: "#E8ECF0", border: "1px solid" }}>
          <Box sx={{ p: 2 }}>
            <TextField size="small" placeholder="Search WPS number, process, prepared by…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment>) }}
              sx={{ width: 340 }} />
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <SmartTable tableId="wps-documents" lockedColumns={["WPS Number", "Rev", "Status"]}>
              <TableHead sx={{ "& .MuiTableCell-head": { backgroundColor: "#1C2636", color: "#FFFFFF", fontWeight: 700, fontSize: 11, letterSpacing: 0.3, textTransform: "uppercase", py: 1.5 } }}>
                <TableRow>
                  <TableCell>WPS Number</TableCell><TableCell>Rev</TableCell><TableCell>Date</TableCell>
                  <TableCell>Process</TableCell><TableCell>Base Metal</TableCell><TableCell>Prepared By</TableCell>
                  <TableCell>Authorized By</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& .MuiTableRow-root:nth-of-type(even)": { backgroundColor: "#F7F9FC" }, "& .MuiTableCell-body": { borderBottomColor: "#E8ECF0", py: 1.5 } }}>
                {loading ? (
                  <TableRow><TableCell colSpan={9} align="center">Loading…</TableCell></TableRow>
                ) : docs.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center">No WPS documents found</TableCell></TableRow>
                ) : (
                  docs.map((d) => {
                    const isSuperseded = d.approvalStatus === "superseded";
                    const isDraft = d.approvalStatus === "pending" || !d.approvalStatus;
                    const statusLabel = isSuperseded ? "Superseded" : isDraft ? "Draft" : "Approved";
                    const statusKind = isSuperseded ? "neutral" : isDraft ? "warning" : "success";
                    return (
                      <TableRow key={d.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ color: "#141A24" }}>{d.wpsNumber}</Typography>
                          <Typography variant="caption" sx={{ color: "#959EA8", fontSize: 10 }}>AWS D1.1 Clause 3</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.revision || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ color: "#555F6B" }}>{d.wpsDate || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{d.process || "—"}</Typography></TableCell>
                        <TableCell>{d.baseMetalSpec || "—"}</TableCell>
                        <TableCell>{d.preparedBy || "—"}</TableCell>
                        <TableCell>{d.authorizedBy || "—"}</TableCell>
                        <TableCell><Tooltip title={d.approvedAt ? `Approved by ${d.approvedByName || "user " + d.approvedBy} on ${String(d.approvedAt).slice(0, 10)}` : ""}><span><StatusPill label={statusLabel} kind={statusKind} /></span></Tooltip></TableCell>
                        <TableCell align="right">
                          <Tooltip title="Print PDF">
                            <IconButton size="small" onClick={() => handlePrint(d)}><Iconify icon="eva:printer-fill" /></IconButton>
                          </Tooltip>
                          {isDraft && canApprove && (
                            <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => { setApproveError(""); setApproveTarget(d); }}><Iconify icon="eva:checkmark-circle-2-fill" /></IconButton></Tooltip>
                          )}
                          {isDraft ? (
                          <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpen(d)}><Iconify icon="eva:edit-fill" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(d)}><Iconify icon="eva:trash-2-fill" /></IconButton>
                          </Tooltip>
                          </>
                          ) : (
                            <Tooltip title="Approved records are locked. Create a new revision to change it."><IconButton size="small" disabled><Iconify icon="eva:lock-fill" /></IconButton></Tooltip>
                          )}
                        </TableCell>
                      </TableRow>);
                  })
                )}
              </TableBody>
            </SmartTable>
          </TableContainer>
        </Card>
      </Container>

      {/* Add / Edit Dialog — mirrors the WeldAssure app WPS form */}
      <Dialog open={openForm} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <BrandedDialogHeader title={editing ? `Edit WPS — ${editing.wpsNumber}` : "New WPS Document"} subtitle="Welding Procedure Specification · AWS D1.1 Clause 3" code="AWS D1.1:2020" onClose={handleClose} />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={1.5}>
            <Sec>Header</Sec>
            <Fld label="WPS Number *" field="wpsNumber" sm={4} />
            <Fld label="Revision" field="revision" sm={2} />
            <Fld label="Date" field="wpsDate" sm={3} type="date" InputLabelProps={{ shrink: true }} />
            <Fld label="Process(es)" field="process" sm={3} placeholder="e.g. MCAW, GMAW" />
            <Fld label="Prepared By" field="preparedBy" sm={4} />
            <Fld label="Authorized By" field="authorizedBy" sm={4} />
            <Fld label="Auth. Date" field="authDate" sm={4} type="date" InputLabelProps={{ shrink: true }} />
            <Fld label="Type" field="type" sm={3} select options={TYPES} />
            <Fld label="AWS Joint Detail" field="awsJointDetail" sm={3} placeholder="e.g. B-U2a" />
            <Fld label="Back Gouge" field="backGouge" sm={3} select options={YN} />
            <Fld label="Supporting PQR(s)" field="supportingPQRs" sm={3} />
            <Grid item xs={12} sm={3}>
              <FormControlLabel control={<Checkbox checked={!!formData.prequalified} onChange={(e) => setFormData((p) => ({ ...p, prequalified: e.target.checked }))} />} label="Prequalified" />
            </Grid>

            <Sec>Joint Design</Sec>
            <Fld label="Joint Type" field="jointType" placeholder="e.g. Butt, T-Joint" />
            <Fld label="Backing" field="jointBacking" select options={YN} />
            <Fld label="Backing Material" field="backingMaterial" />
            <Fld label="Sides" field="sides" select options={SIDES} />
            <Fld label="Root Opening" field="rootOpening" />
            <Fld label="Root Face" field="rootFace" />
            <Fld label="Groove Angle" field="grooveAngle" />
            <Fld label="Radius (J-U)" field="radiusJU" />

            <Sec>Base Metals</Sec>
            <Fld label="Spec. (From)" field="specFrom" placeholder="e.g. Group I" />
            <Fld label="Grade (From)" field="gradeFrom" placeholder="e.g. A36" />
            <Fld label="Spec. (To)" field="specTo" />
            <Fld label="Grade (To)" field="gradeTo" />
            <Fld label="Base Metal Spec (summary)" field="baseMetalSpec" sm={3} />
            <Fld label="Groove Thickness" field="grooveThickness" />
            <Fld label="Fillet Size" field="filletSize" />
            <Fld label="Pipe Diameter" field="pipeDiameter" />

            <Sec>Filler Metals</Sec>
            <Fld label="AWS Spec." field="awsSpec" placeholder="e.g. AWS A5.18" />
            <Fld label="AWS Class." field="awsClass" placeholder="e.g. E70C-6M H4" />
            <Fld label="SFA Spec." field="sfaSpec" />
            <Fld label="Alt. Class." field="altClass" />

            <Sec>Shielding</Sec>
            <Fld label="Flux" field="flux" />
            <Fld label="Gas" field="gas" placeholder="e.g. Argon & CO2" />
            <Fld label="Composition" field="gasComposition" sm={3} placeholder="e.g. 90% Ar / 10% CO2" />
            <Fld label="Flow Rate CFH" field="flowRateCFH" />
            <Fld label="Gas Cup Size" field="gasCupSize" />
            <Fld label="Electrode-Flux" field="electrodeFlux" />

            <Sec>Preheat / Interpass (°F)</Sec>
            <Fld label="Preheat Min." field="preheatMin" />
            <Fld label="Up to 3/4&quot;" field="preheatUpTo34" />
            <Fld label="Over 3/4–1-1/2&quot;" field="preheatOver34" />
            <Fld label="Over 1-1/2–2-1/2&quot;" field="preheatOver112" />
            <Fld label="Over 2-1/2&quot;" field="preheatOver212" />
            <Fld label="Interpass Min." field="interpassMin" />
            <Fld label="Interpass Max." field="interpassMax" />

            <Sec>Position</Sec>
            <Fld label="Groove" field="groovePos" placeholder="e.g. F, O only" />
            <Fld label="Fillet" field="filletPos" />
            <Fld label="Progression" field="progression" select options={PROG} />

            <Sec>Electrical Characteristics</Sec>
            <Fld label="Transfer Mode" field="transferMode" select options={XFER} />
            <Fld label="Current" field="current" select options={CUR} />
            <Fld label="Other" field="otherElec" />
            <Fld label="Tungsten Size" field="tungstenSize" />
            <Fld label="Tungsten Type" field="tungstenType" />

            <Sec>Technique</Sec>
            <Fld label="Stringer/Weave" field="stringerWeave" />
            <Fld label="Multi/Single Pass" field="multiSinglePass" />
            <Fld label="# Electrodes" field="numElectrodes" />
            <Fld label="CTWD" field="ctwd" />
            <Fld label="Angle" field="angle" />
            <Fld label="Peening" field="peening" />
            <Fld label="Interpass Clean" field="interpassClean" />

            <Sec>PWHT</Sec>
            <Fld label="Required" field="pwhtRequired" select options={YN} />
            <Fld label="Temp. (°F)" field="pwhtTemp" />
            <Fld label="Time" field="pwhtTime" />

            <Sec>Welding Procedure (representative pass)</Sec>
            <Fld label="Heat Input" field="heatInput" placeholder="e.g. 25.0 – 56.7 kJ/in" sm={3} />
            <Fld label="Layer/Pass" field="procLayerPass" placeholder="e.g. All" sm={2} />
            <Fld label="Process" field="procProcess" sm={2} />
            <Fld label="Filler Class" field="procFillerClass" sm={2} />
            <Fld label="Dia." field="procDia" sm={2} />
            <Fld label="Cur. Type" field="procCurType" sm={2} />
            <Fld label="Amps/WFS" field="procAmps" sm={2} />
            <Fld label="Volts" field="procVolts" sm={2} />
            <Fld label="Travel IPM" field="procTravel" sm={3} />

            <Sec>Notes</Sec>
            <Grid item xs={12}>
              <TextField label="Notes" value={formData.notes} onChange={f("notes")} fullWidth multiline rows={2} size="small" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#F7F9FC", borderTop: "1px solid #E8ECF0", px: 3, py: 1.75 }}>
          <Button onClick={handleClose} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitLoading || !formData.wpsNumber.trim()}>
            {submitLoading ? "Saving…" : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!approveTarget} onClose={() => !approving && setApproveTarget(null)} maxWidth="xs">
        <DialogTitle>Approve WPS {approveTarget?.wpsNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Your name and today's date are recorded on this WPS and in the audit log. Once approved, the record is locked; a change needs a new revision. The person who created the record cannot approve it.</Typography>
          {approveError && <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{approveError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)} disabled={approving}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={approving}>{approving ? "Approving…" : "Approve"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete WPS?</DialogTitle>
        <DialogContent><Typography>Permanently remove WPS <strong>{deleteTarget?.wpsNumber}</strong>?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
